import type { MetaFunction } from "@remix-run/cloudflare";
import { Link } from "@remix-run/react";
import { 
  AlertTriangle, 
  ArrowLeft,
  Shield,
  CheckCircle,
  Clock,
  Eye,
  ExternalLink,
  FileText,
  Search,
  Upload,
  XCircle
} from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "File Security Guide - Mod Safety with VirusTotal" },
    { 
      name: "description", 
      content: "Complete guide to scanning mod files for security threats using VirusTotal. Learn how to verify file safety before installation and protect your system from malicious content." 
    },
    { name: "keywords", content: "file security,virustotal,mod safety,malware scan,antivirus,security check" },
    { name: "robots", content: "index,follow" },
    { rel: "canonical", href: "https://stellarblademod.com/wiki/knowledge-base/file-security" },
    
    // Open Graph
    { property: "og:title", content: "File Security Guide - Mod Safety with VirusTotal" },
    { property: "og:description", content: "Complete guide to scanning mod files for security threats using VirusTotal. Learn how to verify file safety before installation and protect your system from malicious content." },
    { property: "og:url", content: "https://stellarblademod.com/wiki/knowledge-base/file-security" },
    { property: "og:type", content: "article" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "File Security Guide - Mod Safety with VirusTotal" },
    { name: "twitter:description", content: "Complete guide to scanning mod files for security threats using VirusTotal. Learn how to verify file safety before installation and protect your system from malicious content." },
  ];
};

export default function FileSecurity() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Header */}
      <div className="relative pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm mb-8">
            <Link to="/wiki" className="text-cyan-400 hover:text-cyan-300">
              Wiki
            </Link>
            <span className="text-gray-500">/</span>
            <Link to="/wiki/knowledge-base" className="text-cyan-400 hover:text-cyan-300">
              Knowledge Base
            </Link>
            <span className="text-gray-500">/</span>
            <span className="text-gray-300">File Security</span>
          </nav>

          {/* Back Button */}
          <Link 
            to="/wiki/knowledge-base"
            className="inline-flex items-center text-cyan-400 hover:text-cyan-300 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Knowledge Base
          </Link>

          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <AlertTriangle className="w-12 h-12 text-red-400 mr-4" />
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                  File Security Guide
                </h1>
                <p className="text-xl text-gray-400">Verify Mod Safety with VirusTotal</p>
              </div>
            </div>
            
            <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
              Learn how to scan and verify mod files for security threats before installation
            </p>

            {/* Article Meta */}
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-400 mb-8">
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                <span>10 min read</span>
              </div>
              <div className="flex items-center">
                <Eye className="w-4 h-4 mr-1" />
                <span>12.1k views</span>
              </div>
              <div className="flex items-center">
                <Shield className="w-4 h-4 mr-1" />
                <span>Security Guide</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Introduction */}
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <AlertTriangle className="w-6 h-6 mr-2 text-red-400" />
            Why File Security Matters
          </h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              When downloading mods from the internet, there's always a risk of encountering malicious files. 
              Cybercriminals sometimes disguise malware as popular mod files to infect users' computers.
            </p>
            <p>
              <strong className="text-red-400">If you have concerns about file security</strong>, it's recommended to upload 
              mod files to the <a href="https://www.virustotal.com/gui/home/upload" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">VirusTotal website</a> for 
              multi-scan verification before installation. This website uses <strong>70+ antivirus engines</strong> to scan files and provides detailed security reports.
            </p>
          </div>
        </div>

        {/* What is VirusTotal */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Shield className="w-6 h-6 mr-2 text-blue-400" />
            What is VirusTotal?
          </h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              <a href="https://www.virustotal.com/gui/home/upload" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline font-semibold">VirusTotal</a> is 
              a free online service owned by Google that analyzes files and URLs for viruses, worms, trojans, and other malicious content.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="bg-blue-900/20 border-l-4 border-blue-400 p-4 rounded">
                <h3 className="font-semibold mb-2 text-blue-300">Key Features</h3>
                <ul className="text-sm space-y-1 list-disc list-inside ml-4">
                  <li>70+ antivirus engine scanning</li>
                  <li>Detailed threat analysis reports</li>
                  <li>File behavior monitoring</li>
                  <li>Community-driven threat intelligence</li>
                  <li>Free for public use</li>
                </ul>
              </div>
              
              <div className="bg-green-900/20 border-l-4 border-green-400 p-4 rounded">
                <h3 className="font-semibold mb-2 text-green-300">Benefits</h3>
                <ul className="text-sm space-y-1 list-disc list-inside ml-4">
                  <li>Comprehensive multi-engine detection</li>
                  <li>Zero cost security verification</li>
                  <li>Fast scanning results</li>
                  <li>No software installation required</li>
                  <li>Industry-standard reliability</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* How to Use VirusTotal */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Upload className="w-6 h-6 mr-2 text-green-400" />
            How to Scan Files with VirusTotal
          </h2>
          
          <div className="space-y-6">
            <div className="border-l-4 border-green-400 pl-6">
              <h3 className="text-xl font-semibold text-white mb-2">Step 1: Visit VirusTotal</h3>
              <p className="text-gray-300 mb-3">
                Go to <a href="https://www.virustotal.com/gui/home/upload" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline inline-flex items-center">
                  https://www.virustotal.com/gui/home/upload <ExternalLink className="w-4 h-4 ml-1" />
                </a> in your web browser.
              </p>
            </div>
            
            <div className="border-l-4 border-cyan-400 pl-6">
              <h3 className="text-xl font-semibold text-white mb-2">Step 2: Upload Your File</h3>
              <p className="text-gray-300 mb-3">
                Click the "Choose file" button or drag and drop your mod file into the upload area. 
                VirusTotal supports files up to 650MB in size.
              </p>
            </div>
            
            <div className="border-l-4 border-purple-400 pl-6">
              <h3 className="text-xl font-semibold text-white mb-2">Step 3: Wait for Analysis</h3>
              <p className="text-gray-300 mb-3">
                The scanning process typically takes 1-3 minutes. VirusTotal will analyze your file 
                using multiple antivirus engines simultaneously.
              </p>
            </div>
            
            <div className="border-l-4 border-yellow-400 pl-6">
              <h3 className="text-xl font-semibold text-white mb-2">Step 4: Review Results</h3>
              <p className="text-gray-300 mb-3">
                Examine the scan results carefully. A clean file should show 0 detections from 
                all antivirus engines. Any detections warrant further investigation.
              </p>
            </div>
          </div>
        </div>

        {/* Understanding Results */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Search className="w-6 h-6 mr-2 text-purple-400" />
            Understanding Scan Results
          </h2>
          
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 text-center">
                <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <h3 className="font-semibold text-green-300 mb-2">Clean (0 detections)</h3>
                <p className="text-sm text-gray-300">File appears safe. No threats detected by any engine.</p>
              </div>
              
              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 text-center">
                <AlertTriangle className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <h3 className="font-semibold text-yellow-300 mb-2">Suspicious (1-3 detections)</h3>
                <p className="text-sm text-gray-300">Possible false positives. Research the detections carefully.</p>
              </div>
              
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 text-center">
                <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <h3 className="font-semibold text-red-300 mb-2">Dangerous (4+ detections)</h3>
                <p className="text-sm text-gray-300">Likely malicious. Do not install this file.</p>
              </div>
            </div>
            
            <div className="bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded">
              <h4 className="font-semibold mb-2 text-yellow-300">Important Notes:</h4>
              <ul className="text-sm space-y-1 list-disc list-inside ml-4">
                <li>False positives can occur, especially with game modification tools</li>
                <li>Check detection names - generic names like "Heuristic" are often false positives</li>
                <li>Research specific antivirus engines that flagged the file</li>
                <li>When in doubt, don't install the file</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Shield className="w-6 h-6 mr-2 text-cyan-400" />
            Security Best Practices
          </h2>
          
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-cyan-300 mb-3">Do's</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-1 text-green-400 flex-shrink-0" />
                    <span>Always scan files from unknown sources</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-1 text-green-400 flex-shrink-0" />
                    <span>Download mods from reputable sources</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-1 text-green-400 flex-shrink-0" />
                    <span>Keep your antivirus software updated</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-1 text-green-400 flex-shrink-0" />
                    <span>Create system backups before installing mods</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-1 text-green-400 flex-shrink-0" />
                    <span>Read user reviews and comments</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-red-300 mb-3">Don'ts</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 mt-1 text-red-400 flex-shrink-0" />
                    <span>Don't install files with multiple threat detections</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 mt-1 text-red-400 flex-shrink-0" />
                    <span>Don't disable your antivirus for mod installation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 mt-1 text-red-400 flex-shrink-0" />
                    <span>Don't download from suspicious websites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 mt-1 text-red-400 flex-shrink-0" />
                    <span>Don't ignore security warnings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 mt-1 text-red-400 flex-shrink-0" />
                    <span>Don't run executables from untrusted sources</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Access Link */}
        <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Ready to Scan Your Files?</h3>
          <p className="text-gray-300 mb-6">
            Use VirusTotal to verify the safety of your mod files before installation
          </p>
          <a
            href="https://www.virustotal.com/gui/home/upload"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 shadow-lg"
          >
            <Upload className="w-5 h-5 mr-2" />
            Open VirusTotal
            <ExternalLink className="w-4 h-4 ml-2" />
          </a>
        </div>
      </div>
    </div>
  );
}